import type { ReportColumn } from '../../models/column.types'

import type { BuildCategoricalChartOptionsInput } from './chartBuilder.types'

import { pct } from '@/shared/lib/format'
import { ensureFontAwesomeKit } from '@/shared/ui/icon/fontAwesomeLoader'

function escapeTooltipText(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export function chartColumn(
  input: BuildCategoricalChartOptionsInput,
  field: string,
): ReportColumn | undefined {
  return input.columns?.[field]
}

/**
 * A metric with no owning platform of its own (no `headerImage`/`icon`) borrows the section's
 * channel, mirroring `MetricView`'s table-header fallback (`shared/ui/metric-view/MetricView.vue`).
 * Calculated (Agg.) metrics are excluded — they have no single owning channel. The result feeds
 * both the Highcharts legend (`series.name`, unescaped) and tooltip rows (HTML-escaped by
 * `buildChartTooltip`), so it must stay plain text — no markup.
 */
function channelSuffix(
  input: BuildCategoricalChartOptionsInput,
  column: ReportColumn | undefined,
): string {
  if (!input.channel || column?.isCalculated || column?.numeric !== true) return ''
  if (column?.headerImage?.trim() || column?.icon) return ''
  return ` · ${input.channel}`
}

export function chartFieldLabel(input: BuildCategoricalChartOptionsInput, field: string): string {
  const column = chartColumn(input, field)
  const header = column?.header ?? input.metricLabel(field)
  return `${header}${channelSuffix(input, column)}`
}

export function formatChartValue(
  input: BuildCategoricalChartOptionsInput,
  value: unknown,
  field: string,
  returnStr = true,
): string {
  if (input.formatValue) return input.formatValue(value, field, returnStr)
  return String(value ?? '-')
}

function trimmed(value: number): string {
  return String(Math.trunc(value * 100) / 100)
}

export function formatChartAxisValue(
  input: BuildCategoricalChartOptionsInput,
  value: number,
  field: string,
): string {
  const column = chartColumn(input, field)
  if (column?.percentage || column?.sub_type === 'percentage') {
    return `${trimmed(value * 100)}%`
  }
  const absolute = Math.abs(value)
  if (absolute >= 1e9) return `${trimmed(value / 1e9)}B`
  if (absolute >= 1e6) return `${trimmed(value / 1e6)}M`
  if (absolute >= 1e3) return `${trimmed(value / 1e3)}k`
  return trimmed(value)
}

function axisGroup(column: ReportColumn | undefined): string {
  if (column?.percentage || column?.sub_type === 'percentage') return 'percentage'
  if (column?.currency || column?.sub_type === 'currency') return 'currency'
  if (column?.sub_type === 'meta') return 'numeric'
  return column?.sub_type ?? (column?.numeric ? 'numeric' : 'default')
}

export function buildChartMetricAxes(
  input: BuildCategoricalChartOptionsInput,
  metrics: readonly string[],
): { yAxis: Array<Record<string, unknown>>; metricAxisIndex: Map<string, number> } {
  const groups: Array<{ key: string; metrics: string[] }> = []
  const metricAxisIndex = new Map<string, number>()
  for (const metric of metrics) {
    const key = axisGroup(chartColumn(input, metric))
    let index = groups.findIndex((group) => group.key === key)
    if (index < 0) {
      groups.push({ key, metrics: [] })
      index = groups.length - 1
    }
    groups[index]!.metrics.push(metric)
    metricAxisIndex.set(metric, index)
  }

  return {
    metricAxisIndex,
    yAxis: groups.map((group, index) => {
      const axisMetric = group.metrics[0]!
      return {
        visible: groups.length <= 2,
        opposite: index % 2 === 1,
        title: {
          text: group.metrics.map((metric) => chartFieldLabel(input, metric)).join(' / '),
          style: {
            color: 'var(--ui-chart-axis-title)',
            fontSize: '12px',
            fontWeight: '700',
          },
        },
        labels: {
          style: {
            color: 'var(--ui-chart-label)',
            fontSize: '11px',
            fontWeight: '400',
          },
          formatter(this: { value: number }) {
            return formatChartAxisValue(input, Number(this.value), axisMetric)
          },
        },
      }
    }),
  }
}

const NEGATIVE_METRICS = new Set([
  'cost',
  'spend',
  'cpc',
  'cpm',
  'cps',
  'cpi',
  'cpa',
  'cost_per_conversion',
  'cost_per_purchase',
  'bounce_rate',
  'exit_rate',
  'frequency',
])

export function isNegativeMetric(metric: string, column?: ReportColumn): boolean {
  if (column?.change_type === 'negative') return true
  if (column?.change_type === 'positive') return false
  const normalized = metric.toLowerCase()
  return (
    NEGATIVE_METRICS.has(normalized) ||
    normalized.endsWith('_cost') ||
    normalized.endsWith('_cpc') ||
    normalized.endsWith('_cpa')
  )
}

export interface ChartTooltipRow {
  color?: unknown
  label: unknown
  value: unknown
}

function resolveDeltaRatio(
  actual: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (
    actual == null ||
    previous == null ||
    !Number.isFinite(actual) ||
    !Number.isFinite(previous)
  ) {
    return null
  }
  if (previous === 0) {
    return actual === 0 ? 0 : null
  }
  const ratio = (actual - previous) / Math.abs(previous)
  return Number.isFinite(ratio) ? ratio : null
}

function resolveDeltaTone(
  isZero: boolean,
  isUp: boolean,
  isNegative: boolean,
): 'success' | 'danger' | 'neutral' {
  if (isZero) return 'neutral'
  if (isUp) return isNegative ? 'danger' : 'success'
  return isNegative ? 'success' : 'danger'
}

function deltaIconMarkup(isZero: boolean, isUp: boolean): string {
  // Mirrors the Icon component's `fa-{weight} fa-{glyph}` output (see Icon.vue) — the tooltip
  // is raw HTML injected by Highcharts, so it can't render the Vue component itself, but the
  // Font Awesome kit script it depends on is already loaded globally by any on-page Icon.
  const glyph = isZero ? 'fa-minus' : isUp ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'
  return `<i class="fa-regular ${glyph} inline-block h-3.5 w-3.5 flex-none text-center" style="font-size:0.875rem;line-height:0.875rem" aria-hidden="true"></i>`
}

const DELTA_TONE_CLASSES = {
  success: 'bg-success-soft text-success',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-sunken text-muted',
} as const

export function buildChartTooltipChangeBadge(
  actualValue: number | null | undefined,
  previousValue: number | null | undefined,
  isNegative = false,
): string {
  const ratio = resolveDeltaRatio(actualValue, previousValue)
  if (ratio === null) return ''

  ensureFontAwesomeKit()
  const isZero = Math.abs(ratio) < 0.00005
  const isUp = ratio > 0 && !isZero
  const tone = resolveDeltaTone(isZero, isUp, isNegative)
  const icon = deltaIconMarkup(isZero, isUp)
  const formattedPercent = pct.formatPercent(Math.abs(ratio), 'en-US')

  return (
    '<div class="ui-chart-tooltip__change mt-xs flex justify-center">' +
    `<span class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${DELTA_TONE_CLASSES[tone]}">` +
    `${icon}` +
    `<span class="font-numeric tabular-nums">${escapeTooltipText(formattedPercent)}</span>` +
    '</span>' +
    '</div>'
  )
}

export function buildChartTooltip(
  title: unknown,
  rows: readonly ChartTooltipRow[],
  changeBadgeHtml?: string,
): string {
  return [
    '<div class="ui-chart-tooltip grid min-w-52 gap-xs rounded-md border border-border bg-surface p-sm font-sans text-xs text-fg shadow-pop">',
    `<div class="ui-chart-tooltip__title border-b border-border pb-2xs font-bold">${escapeTooltipText(title)}</div>`,
    ...rows.map(
      (row) =>
        '<div class="ui-chart-tooltip__row grid grid-cols-[0.5rem_minmax(0,1fr)_auto] items-center gap-xs">' +
        `<span class="ui-chart-tooltip__dot h-xs w-xs rounded-full" style="background:${escapeTooltipText(row.color ?? 'var(--ui-color-muted)')}"></span>` +
        `<span class="ui-chart-tooltip__label min-w-0 truncate">${escapeTooltipText(row.label)}</span>` +
        `<strong class="ui-chart-tooltip__value font-numeric tabular-nums">${escapeTooltipText(row.value)}</strong>` +
        '</div>',
    ),
    changeBadgeHtml ?? '',
    '</div>',
  ].join('')
}
